import {FiltersBody} from '@dms/api/core';

import {ApiEnumItem} from '../../../types/ApiEnum';
import {DEFAULT_FILTERS, FiltersType} from '../types/FiltersType';
import {migrateFiltersFromOldStructure, OldFiltersType} from './migrateFiltersFromOldStructure';
import {readAuctionTypeFilter} from './readAuctionTypeFilter';

const isFiltersBody = (input: FiltersBody | FiltersType | OldFiltersType): input is FiltersBody => {
  if (!input) {
    return false;
  }

  const hasSelectedColors = 'selectedColors' in input && input.selectedColors !== undefined;

  if (hasSelectedColors && Array.isArray(input.selectedColors) && input.selectedColors.length > 0) {
    const firstColor = input.selectedColors[0];
    return 'name' in firstColor && !('value' in firstColor) && !('label' in firstColor);
  }

  const hasRangeAsArray =
    ('year' in input && Array.isArray(input.year) && input.year.length > 2) ||
    ('mileageRange' in input &&
      Array.isArray(input.mileageRange) &&
      input.mileageRange.length > 2) ||
    ('power' in input && Array.isArray(input.power) && input.power.length > 2);

  return hasRangeAsArray;
};

const convertRangeArrayToTuple = (
  range: number[] | null | undefined
): [number, number] | undefined => {
  if (!range || !Array.isArray(range) || range.length < 2) {
    return undefined;
  }
  return [range[0], range[1]];
};

const convertSelectedColors = (
  colors: FiltersBody['selectedColors']
): ApiEnumItem[] | undefined => {
  if (!colors || !Array.isArray(colors)) {
    return undefined;
  }
  return colors.map((color) => ({
    key: color.key,
    value: color.key,
    label: color.name,
    colorCode: color.colorCode,
  }));
};

const convertNullToUndefined = <T>(value: T | null | undefined): T | undefined =>
  value === null ? undefined : value;

export const convertFiltersBodyToFiltersType = (
  filtersBody: FiltersBody | FiltersType | OldFiltersType
): FiltersType => {
  if (isFiltersBody(filtersBody)) {
    return {
      ...DEFAULT_FILTERS,
      ...filtersBody,
      auctionType: readAuctionTypeFilter(filtersBody).auctionType,
      selectedColors: convertSelectedColors(filtersBody.selectedColors),
      year: convertRangeArrayToTuple(filtersBody.year),
      mileageRange: convertRangeArrayToTuple(filtersBody.mileageRange),
      power: convertRangeArrayToTuple(filtersBody.power),
      cubicCapacity: convertRangeArrayToTuple(filtersBody.cubicCapacity),
      margin: convertRangeArrayToTuple(filtersBody.margin),
      marginPercent: convertRangeArrayToTuple(filtersBody.marginPercent),
      priceCZK: convertRangeArrayToTuple(filtersBody.priceCZK),
      liquidity: convertRangeArrayToTuple(filtersBody.liquidity),
      daysOnDisplay: convertRangeArrayToTuple(filtersBody.daysOnDisplay),
      numberOfSeats: convertRangeArrayToTuple(filtersBody.numberOfSeats),
      avgNumberAds: convertRangeArrayToTuple(filtersBody.avgNumberAds),
      featureScore: convertRangeArrayToTuple(filtersBody.featureScore),
      reclaimableVat: convertNullToUndefined(filtersBody.reclaimableVat),
      hitlistOnly: convertNullToUndefined(filtersBody.hitlistOnly),
      hideBlacklist: convertNullToUndefined(filtersBody.hideBlacklist),
    };
  }

  return migrateFiltersFromOldStructure(filtersBody);
};
